import { useState } from "react";
import { skipToken } from "@reduxjs/toolkit/query";
import { Card, CardBody, Container } from "reactstrap";

import IconComponent from "../../../CommonElements/IconComponent";
import {
  useGetCentersListQuery,
  useGetDepartmentListQuery,
  useGetEmployeeJobTypesListQuery,
} from "../../../ReduxToolkit/api/organization";
import { useEmployeeMasterReportListQuery } from "../../../ReduxToolkit/api/reports";
import { useGetMenusListQuery } from "../../../ReduxToolkit/api/menus";
import useDebounce from "../../../hooks/useDebouceSearch";
import UserMenuForm from "./form/UserMenuForm";
import "./AssignUserMenuContainer.scss";

const defaultUserFilters = {
  search: "",
  status: "1",
  jobType: "",
  departmentId: "",
  centerId: "",
  dateFilter: "Select",
  dateRange: [],
};

const formatDate = (date) => (date?.format ? date.format("YYYY-MM-DD") : "");

const toMasterList = (responseData) =>
  Array.isArray(responseData) ? responseData : responseData?.data ?? [];

const dedupeUsersById = (users) => {
  const userMap = new Map();

  users.forEach((user) => {
    if (user?.id && !userMap.has(user.id)) {
      userMap.set(user.id, user);
    }
  });

  return Array.from(userMap.values());
};

const AssignUserMenuContainer = () => {
  const [userFilters, setUserFilters] = useState(defaultUserFilters);
  const debouncedUserSearch = useDebounce(userFilters.search, 600);

  const {
    data: menusData,
    isFetching: isMenusFetching,
    isLoading: isMenusLoading,
  } = useGetMenusListQuery(undefined);

  const {
    data: departmentListData,
    isFetching: isDepartmentListFetching,
    isLoading: isDepartmentListLoading,
  } = useGetDepartmentListQuery({ status: "active" });
  const {
    data: centerListData,
    isFetching: isCenterListFetching,
    isLoading: isCenterListLoading,
  } = useGetCentersListQuery(undefined);
  const {
    data: employeeJobTypesList,
    isFetching: isJobTypesListFetching,
    isLoading: isJobTypesListLoading,
  } = useGetEmployeeJobTypesListQuery(undefined);

  const userReportParams = (() => {
    const params = { page: 1, size: 1000 };

    if (debouncedUserSearch) params.name = debouncedUserSearch;
    if (userFilters.status) params.status = Number(userFilters.status);
    if (userFilters.jobType) params.jobType = userFilters.jobType;
    if (userFilters.departmentId)
      params.departmentId = Number(userFilters.departmentId);
    if (userFilters.centerId) params.centerId = Number(userFilters.centerId);

    const [startDate, endDate] = userFilters.dateRange ?? [];
    if (userFilters.dateFilter !== "Select" && startDate && endDate) {
      const start = formatDate(startDate);
      const end = formatDate(endDate);

      if (start && end) {
        if (userFilters.dateFilter === "doj") {
          params.doj = true;
          params.dojStartDate = start;
          params.dojEndDate = end;
        }
        if (userFilters.dateFilter === "dob") {
          params.dob = true;
          params.dobStartDate = start;
          params.dobEndDate = end;
        }
        if (userFilters.dateFilter === "anniversary") {
          params.anniversary = true;
          params.anniversaryStartDate = start;
          params.anniversaryEndDate = end;
        }
        if (userFilters.dateFilter === "exit") {
          params.exit = true;
          params.exitStartDate = start;
          params.exitEndDate = end;
        }
      }
    }

    return params;
  })();

  const {
    data: employeeMasterReportList,
    isFetching: isUsersFetching,
    isLoading: isUsersLoading,
  } = useEmployeeMasterReportListQuery(userReportParams ?? skipToken);
  const menus = menusData?.data ?? [];
  const users = dedupeUsersById(toMasterList(employeeMasterReportList?.data));
  const userTotalCount = employeeMasterReportList?.count ?? users.length;

  const jobTypeOptions =
    employeeJobTypesList?.data?.map((jobType) => ({
      label: `${jobType.name}-${jobType.type}`,
      value: jobType.id,
    })) ?? [];
  const departmentOptions =
    departmentListData?.data?.map(({ id, name }) => ({
      label: name,
      value: id,
    })) ?? [];
  const centerOptions =
    centerListData?.data?.map(({ id, name }) => ({
      label: name,
      value: id,
    })) ?? [];

  const updateUserFilters = (nextFilters) => {
    setUserFilters((currentFilters) => ({
      ...currentFilters,
      ...nextFilters,
    }));
  };

  return (
    <Container fluid className="assign-user-menu-page">
      <Card>
        <CardBody>
          <UserMenuForm
            menus={menus}
            users={users}
            userTotalCount={userTotalCount}
            userFilters={userFilters}
            userFilterOptions={{
              jobTypeOptions,
              departmentOptions,
              centerOptions,
            }}
            filtersLoading={
              isJobTypesListLoading ||
              isJobTypesListFetching ||
              isDepartmentListLoading ||
              isDepartmentListFetching ||
              isCenterListLoading ||
              isCenterListFetching
            }
            usersLoading={isUsersLoading || isUsersFetching}
            onUserFiltersChange={updateUserFilters}
          />
          {(isMenusLoading || isMenusFetching) && (
            <div className="assign-user-menu-page__loading">
              <IconComponent className="fa fa-spinner fa-spin" />
              Loading menus...
            </div>
          )}
        </CardBody>
      </Card>
    </Container>
  );
};

export default AssignUserMenuContainer;
